import React, { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { UserIcon, KeyIcon, ShieldCheckIcon, ClockIcon, SettingsIcon, ChevronRightIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { apiClient, AdminUpdateRequest } from '../lib/api'

interface ProfileFormData {
  email: string
}

interface PasswordFormData {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export default function Settings() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const [profileData, setProfileData] = useState<ProfileFormData>({
    email: user?.email || ''
  })

  // Update profile data when user data changes
  React.useEffect(() => {
    if (user?.email) {
      setProfileData(prev => ({ ...prev, email: user.email }))
    }
  }, [user?.email])

  const [passwordData, setPasswordData] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Profile update mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: AdminUpdateRequest) => apiClient.updateCurrentAdmin(data),
    onSuccess: () => {
      setProfileMessage({ type: 'success', text: 'Profile updated successfully!' })
      queryClient.invalidateQueries({ queryKey: ['currentAdmin'] })
      setTimeout(() => setProfileMessage(null), 5000)
    },
    onError: (error: Error) => {
      setProfileMessage({ type: 'error', text: error.message || 'Failed to update profile' })
      setTimeout(() => setProfileMessage(null), 5000)
    }
  })

  // Password update mutation
  const updatePasswordMutation = useMutation({
    mutationFn: (data: AdminUpdateRequest) => apiClient.updateCurrentAdmin(data),
    onSuccess: () => {
      setPasswordMessage({ type: 'success', text: 'Password updated successfully!' })
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
      setTimeout(() => setPasswordMessage(null), 5000)
    },
    onError: (error: Error) => {
      setPasswordMessage({ type: 'error', text: error.message || 'Failed to update password' })
      setTimeout(() => setPasswordMessage(null), 5000)
    }
  })

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (profileData.email === user?.email) {
      setProfileMessage({ type: 'error', text: 'No changes to save' })
      setTimeout(() => setProfileMessage(null), 3000)
      return
    }

    updateProfileMutation.mutate({ email: profileData.email })
  }

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'All fields are required' })
      setTimeout(() => setPasswordMessage(null), 3000)
      return
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' })
      setTimeout(() => setPasswordMessage(null), 3000)
      return
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 8 characters long' })
      setTimeout(() => setPasswordMessage(null), 3000)
      return
    }

    updatePasswordMutation.mutate({
      current_password: passwordData.currentPassword,
      new_password: passwordData.newPassword
    })
  }

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Loading...'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid Date'
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
            Admin Settings
          </h3>

          {/* Profile Information Section */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h4 className="text-md font-medium text-gray-900">Profile Information</h4>
            </div>

            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <div className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md border border-gray-300">
                  {user.role}
                </div>
              </div>

              {profileMessage && (
                <div className={`rounded-md p-4 ${profileMessage.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                  {profileMessage.text}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={updateProfileMutation.isLoading}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {updateProfileMutation.isLoading ? 'Updating...' : 'Update Profile'}
                </button>
              </div>
            </form>
          </div>

          {/* Password Section */}
          <div className="mb-8 border-t border-gray-200 pt-8">
            <div className="flex items-center mb-4">
              <KeyIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h4 className="text-md font-medium text-gray-900">Change Password</h4>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                  Current Password
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
                  required
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
                  minLength={8}
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Must be at least 8 characters long</p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
                  required
                />
              </div>

              {passwordMessage && (
                <div className={`rounded-md p-4 ${passwordMessage.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                  {passwordMessage.text}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={updatePasswordMutation.isLoading}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {updatePasswordMutation.isLoading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>

          {/* Advanced Settings Section */}
          <div className="mb-8 border-t border-gray-200 pt-8">
            <div className="flex items-center mb-4">
              <SettingsIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h4 className="text-md font-medium text-gray-900">Advanced Settings</h4>
            </div>

            <div className="space-y-3">
              <Link
                to="/advanced-settings"
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div>
                  <h5 className="font-medium text-gray-900">AI Chat Model & Base Prompt Templates</h5>
                  <p className="text-sm text-gray-500 mt-1">
                    Choose the AI chat model and manage the foundational AI prompt templates used by all tenants
                  </p>
                </div>
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              </Link>
              <Link
                to="/role-permissions"
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div>
                  <h5 className="font-medium text-gray-900">Role Permissions</h5>
                  <p className="text-sm text-gray-500 mt-1">
                    Manage access levels for admin, manager, and user roles across the platform
                  </p>
                </div>
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              </Link>
            </div>
          </div>

          {/* Account Information Section */}
          <div className="border-t border-gray-200 pt-8">
            <div className="flex items-center mb-4">
              <ShieldCheckIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h4 className="text-md font-medium text-gray-900">Account Information</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Account ID</label>
                <div className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md border border-gray-300">
                  {user.id}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Account Status</label>
                <div className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.is_active === undefined
                    ? 'bg-gray-100 text-gray-800'
                    : user.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                    }`}>
                    {user.is_active === undefined ? 'Loading...' : user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center">
                  <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <label className="block text-sm font-medium text-gray-700">Account Created</label>
                </div>
                <div className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md border border-gray-300">
                  {formatDate(user.created_at)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
